import { useEffect, useRef, useState } from "react";

/**
 * Embedded Tweet Component
 *
 * Embeds a real Twitter/X tweet using Twitter's official embed code.
 * Shows a placeholder while the tweet is loading.
 */

interface EmbeddedTweetProps {
  className?: string;
}

const EmbeddedTweet = ({ className = "" }: EmbeddedTweetProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load Twitter widgets script
    const scriptId = "twitter-widgets-js";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://platform.twitter.com/widgets.js";
      script.charset = "utf-8";
      script.async = true;
      document.body.appendChild(script);
    }

    // Wait for Twitter widgets to be ready and render
    let intervalId: NodeJS.Timeout | null = null;
    let fallbackId: NodeJS.Timeout | null = null;

    const checkTwitterReady = () => {
      if (window.twttr) {
        window.twttr.ready(() => {
          // Clear the interval once twttr is ready
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }

          // Listen for the rendered event
          window.twttr?.events.bind("rendered", (event: unknown) => {
            // Check if the rendered element is within our container
            const renderedElement = (event as { target?: HTMLElement }).target;
            if (renderedElement && containerRef.current?.contains(renderedElement)) {
              setIsLoading(false);
            }
          });

          // Also set a longer fallback (10 seconds)
          fallbackId = setTimeout(() => {
            setIsLoading(false);
          }, 10000);
        });
      }
    };

    // Check immediately and also set up an interval
    checkTwitterReady();
    intervalId = setInterval(checkTwitterReady, 100);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (fallbackId) clearTimeout(fallbackId);
    };
  }, []);

  return (
    <div ref={containerRef} className={`${className}`}>
      {/* Container with relative positioning */}
      <div className="relative max-w-[550px] mx-auto">
        {/* Twitter embed - always takes up space */}
        <blockquote className="twitter-tweet" data-theme="light" style={{ visibility: isLoading ? "hidden" : "visible" }}>
          <a href="https://twitter.com/joely7758521/status/2033107982062698676?ref_src=twsrc%5Etfw"></a>
        </blockquote>

        {/* Skeleton placeholder - absolutely positioned on top */}
        {isLoading && (
          <div className="absolute inset-0 border border-border rounded-lg bg-muted/20 p-4 animate-pulse" style={{ minHeight: "320px" }}>
            <div className="flex items-start gap-3 h-full">
              {/* Avatar */}
              <div className="w-12 h-12 rounded-full bg-muted flex-shrink-0" />
              {/* Content */}
              <div className="flex-1 space-y-4 min-w-0">
                {/* Name and handle */}
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-1/4" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
                {/* Tweet text - taller to match real tweet height */}
                <div className="space-y-2 pt-2">
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </div>
                {/* Actions */}
                <div className="flex gap-6 pt-4">
                  <div className="h-3 bg-muted rounded w-14" />
                  <div className="h-3 bg-muted rounded w-14" />
                  <div className="h-3 bg-muted rounded w-14" />
                  <div className="h-3 bg-muted rounded w-14" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbeddedTweet;
